import { CalEventFieldType } from '../constants/cal-event-field-type.js';
import { startOfUtcDay } from '../customer-account/day-math.js';
import { isBlankFieldValue } from './is-blank-field-value.js';
import { ruleConditions } from './rule-conditions.js';
import { buildRuleDependencyGraph } from './rule-dependency-graph.js';
import { RuleInvariantError } from './rule-invariant-error.js';
import { CORE_FIELD_ID_SET, SETTABLE_CORE_FIELD_IDS, toComparableDate, } from './rule-types.js';
function isKnownFieldId(fieldId, fields) {
    return CORE_FIELD_ID_SET.has(fieldId) || fields.some((field) => field.id === fieldId);
}
function findFieldType(fieldId, fields) {
    return fields.find((field) => field.id === fieldId)?.type;
}
function assertUnreachableRuleType(type, targetFieldId) {
    throw new RuleInvariantError(`evaluateRules: unhandled rule type "${String(type)}" on target "${targetFieldId}" — validateRules should have rejected this rule before it reached evaluation.`);
}
/** `expected` is authored, not user input — a type mismatch here must fail loudly, not silently as "condition not met". */
function assertNumericCondition(operator, expected) {
    if (typeof expected !== 'number') {
        throw new RuleInvariantError(`Rule condition for operator "${operator}" expects a numeric value but got ${typeof expected} — validateRules should have rejected this rule before it reached evaluation.`);
    }
}
function toUtcDayTime(value) {
    const time = toComparableDate(value);
    return time === undefined ? undefined : startOfUtcDay(new Date(time)).getTime();
}
function evaluateCondition({ operator, actual, expected, fieldType, }) {
    switch (operator) {
        case 'eq': {
            if (fieldType !== CalEventFieldType.DATE)
                return actual === expected;
            const actualDay = toUtcDayTime(actual);
            const expectedDay = toUtcDayTime(expected);
            return actualDay !== undefined && expectedDay !== undefined && actualDay === expectedDay;
        }
        case 'gt':
            assertNumericCondition(operator, expected);
            return typeof actual === 'number' && actual > expected;
        case 'gte':
            assertNumericCondition(operator, expected);
            return typeof actual === 'number' && actual >= expected;
        case 'lt':
            assertNumericCondition(operator, expected);
            return typeof actual === 'number' && actual < expected;
        case 'lte':
            assertNumericCondition(operator, expected);
            return typeof actual === 'number' && actual <= expected;
        case 'before': {
            const actualDay = toUtcDayTime(actual);
            const expectedDay = toUtcDayTime(expected);
            return actualDay !== undefined && expectedDay !== undefined && actualDay < expectedDay;
        }
        case 'after': {
            const actualDay = toUtcDayTime(actual);
            const expectedDay = toUtcDayTime(expected);
            return actualDay !== undefined && expectedDay !== undefined && actualDay > expectedDay;
        }
        case 'in':
            if (!Array.isArray(expected)) {
                throw new RuleInvariantError(`Rule condition for operator "in" expects an array value but got ${typeof expected} — validateRules should have rejected this rule before it reached evaluation.`);
            }
            return expected.includes(actual);
        case 'isEmpty':
            return isBlankFieldValue(actual);
        case 'isSet':
            return !isBlankFieldValue(actual);
    }
}
/** Topological order by condition-field dependency, falling back to DFS postorder on a cycle. */
function topologicalRuleOrder(rules) {
    const rulesByTarget = buildRuleDependencyGraph(rules);
    const visited = new Set();
    const visiting = new Set();
    const ordered = [];
    function visit(index) {
        if (visited.has(index) || visiting.has(index))
            return;
        visiting.add(index);
        const rule = rules[index];
        for (const { fieldId } of ruleConditions(rule)) {
            for (const depIndex of rulesByTarget.get(fieldId) ?? [])
                visit(depIndex);
        }
        visiting.delete(index);
        visited.add(index);
        ordered.push(rule);
    }
    rules.forEach((_rule, index) => visit(index));
    return ordered;
}
/** Evaluates rules in dependency order so a defaultValueIf target resolves before rules conditioning on it. */
export function evaluateRules(fields, rules, values) {
    const effectiveValues = { ...values };
    const visibleIfTargets = new Set(rules.filter((rule) => rule.type === 'visibleIf').map((rule) => rule.targetFieldId));
    const result = {
        required: {},
        visible: Object.fromEntries(fields.filter((field) => !visibleIfTargets.has(field.id)).map((field) => [field.id, true])),
        defaults: {},
        tagIds: [],
    };
    for (const rule of topologicalRuleOrder(rules)) {
        const { targetFieldId, type } = rule;
        // An unknown condition field is treated as unsatisfied; validateRules should reject it first.
        const conditionMet = ruleConditions(rule).every((condition) => isKnownFieldId(condition.fieldId, fields)
            ? evaluateCondition({
                operator: condition.operator,
                actual: effectiveValues[condition.fieldId],
                expected: condition.value,
                fieldType: findFieldType(condition.fieldId, fields),
            })
            : false);
        if (type === 'requiredIf') {
            result.required[targetFieldId] = result.required[targetFieldId] === true || conditionMet;
            if (conditionMet && SETTABLE_CORE_FIELD_IDS.includes(targetFieldId)) {
                effectiveValues[targetFieldId] = true;
            }
        }
        else if (type === 'visibleIf') {
            result.visible[targetFieldId] = result.visible[targetFieldId] === true || conditionMet;
        }
        else if (type === 'addTagIf') {
            if (conditionMet && !result.tagIds.includes(targetFieldId))
                result.tagIds.push(targetFieldId);
        }
        else if (type === 'defaultValueIf') {
            if (conditionMet) {
                result.defaults[targetFieldId] = rule.value;
                if (CORE_FIELD_ID_SET.has(targetFieldId) ||
                    isBlankFieldValue(effectiveValues[targetFieldId])) {
                    effectiveValues[targetFieldId] = rule.value;
                }
            }
        }
        else {
            assertUnreachableRuleType(type, targetFieldId);
        }
    }
    return result;
}
//# sourceMappingURL=rule-evaluator.js.map